import { Injectable } from '@angular/core';
import { TypeLoading } from '@app/enums';
import { eligibilityIropsInt } from '@app/interfaces/micro/eligibilityIrops.interface';
import { ApiMapper } from '@app/mappers/micro/api.mapper';
import { Reservation } from '@app/models/micro';
import { catchError, map, Observable, throwError, forkJoin, of, tap, switchMap } from 'rxjs';
import { LoadingService } from '../loading.service';
import { environment } from '@env/environment';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { IROPS_REMARK, IROPS_TYPE } from '@app/utils/constants/generals';
import { IropsMapper } from '@app/mappers';
import { IropsRemarkParamsInt } from '@app/interfaces/micro/irops-remark-params.interface';
import { SearchProductsInt } from '@app/interfaces/micro/searchProductsIrops.interface';
import { SearchFlightInt } from '@app/interfaces/micro/searchFlightIrops.interface';
import { IropsHeadersInt } from '@app/interfaces/micro/irops.interface';
import { getTypeFlowIrops } from '@app/utils';
import { FlightSummaryIrops } from '@app/models/micro/flight-summary-irops.model';
import { ErrorAdapter } from '@models/adapter/error.adapter';
import { CabinInfo, CabinResponse, CabinRquest } from '@app/interfaces/micro/cabin.interface';
import { PNRLookupAWSInt } from '@app/interfaces/reservation-aws.interface';
type Flows = 'checkin' | 'EXCHANGE';
@Injectable({
  providedIn: 'root',
})
export class IropsService {
  urlConfirmation = environment.urlApiSelfReaccom + '/sc/flight/confirmation';
  urlReassociation = environment.urlApiAncillary;
  urlRemark = environment.urlApiSelfReaccom + '/sb/remark';
  urlIrops = environment.urlApiWeb + 'v4/exchanges/am-exchanges-api';
  urlIropsPu = environment.urlApiWeb + 'v1/exchanges/am-exchanges-api';
  urlMicro = `${environment.urlApiCheckinBFF}${environment.versionCheckin}/kiosk/checkin/flightstatus`;
  /** BFF de pagos: payment-revenue-dc-exchange (cifrado, ver encrypted-services.config). */
  urlPaymentBFF = `${environment.urlApiPaymentBFF}${environment.versionPaymentsBFF}/kiosk/payments`;
  /** BFF de exchanges (sin cifrar por ahora): eligibilities y reservations (antes /sc/ del micro). */
  urlExchangesBFF = `${environment.urlApiExchangesBFF}${environment.versionExchangesBFF}/kiosk/exchanges`;
  urlApiSelfReaccom = environment.urlApiSelfReaccom;
  urlApiSelfReacomAncillary = environment.urlApiAncillary;
  pnrData: any;
  channel = 'kiosk';
  iropsHeadersInt: IropsHeadersInt;
  ancillariesData: any;
  bestMatchItinerary: FlightSummaryIrops;
  itineraryOptions: FlightSummaryIrops[];
  flightSelect: FlightSummaryIrops;
  hasConfirmed: boolean = false;
  originalReservation: Reservation = null;
  cabins: CabinInfo[] = [];
  affectationType: string = 'MAJ';
  hasEntryDataLoaded: boolean = false;

  constructor(private http: HttpClient, private _loadingService: LoadingService) {}

  resetIropsState() {
    this.hasConfirmed = false;
    this.originalReservation = null;
    this.hasEntryDataLoaded = false;
  }

  getHeaders(reservation: Reservation, flow: Flows = 'checkin', ssr?: string) {
    let headers = new HttpHeaders({
      channel: this.channel,
      flow: flow,
      cacheEnabled: 'false',
      'x-transactionId': reservation.flightSummary.cartId,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
      store: 'mx',
      reservationCode: reservation.pnr,
      execution: this.iropsHeadersInt?.executionApp || '',
      CookieApp: this.iropsHeadersInt?.cookieApp || '',
      iropsType: this.iropsHeadersInt?.iropsType || '',
      involuntaryExchange: this.iropsHeadersInt?.involuntaryExchange || 'true',
      pnr: reservation.pnr,
      type: IROPS_TYPE,
      remark: ssr ? ssr : IROPS_REMARK,
    });

    return headers;
  }

  getHeadersConfirmation(reservation: Reservation, flow: Flows = 'checkin') {
    let headers = new HttpHeaders({
      channel: this.channel,
      'x-transactionId': reservation.flightSummary.cartId,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
      pnr: reservation.pnr,
    });

    return headers;
  }

  eligibility(
    reservation: Reservation,
    load = TypeLoading.FULL_LOADING
  ): Observable<eligibilityIropsInt> {
    const body = ApiMapper.mapEligibilityIrops(reservation);
    const headers = this.getHeaders(reservation);

    return this.http
      .post<eligibilityIropsInt>(`${this.urlExchangesBFF}/eligibilities`, body, {
        headers,
      })
      .pipe(catchError((error) => this.handlerError(error, load)));
  }

  pnr(reservation: Reservation): Observable<any> {
    const headers = this.getHeaders(reservation);

    return this.http
      .get(`${this.urlExchangesBFF}/reservations`, { headers, observe: 'response' })
      .pipe(
        map((response) => {
          this.iropsHeadersInt = {
            cookieApp: response.headers.get('Cookie'),
            executionApp: response.headers.get('execution'),
            iropsType: getTypeFlowIrops(reservation),
            involuntaryExchange: 'true',
          };
          return response;
        }),
        catchError((error) => this.handlerError(error, TypeLoading.FULL_LOADING))
      );
  }

  removeCHKN(reservation: Reservation): any {
    const body = IropsMapper.getRquestRemoveCHKN(reservation);
    const headers = this.getHeaders(reservation);

    return this.http
      .post(`${this.urlApiSelfReaccom}/sc/pax/offload/removeCheckin`, body, { headers })
      .pipe(
        map((response) => {
          return IropsMapper.getResponseRemoveCHKN(response);
        }),
        catchError((error) => this.handlerError(error, TypeLoading.FOOTER_LOADING))
      );
  }

  searchFlight(
    reservation: Reservation,
    departureDate: string = undefined,
    cabin?: CabinInfo[]
  ): Observable<SearchFlightInt> {
    const body = ApiMapper.mapSearchFlightIrops(reservation, departureDate, cabin);
    const rawCookie = this.iropsHeadersInt?.cookieApp ?? '';
    const cookieFormatted = rawCookie
      .split(',')
      .map((c) => c.trim())
      .join('; ');
    const headers = this.getHeaders(reservation).set('CookieApp', cookieFormatted);
    this.bestMatchItinerary = undefined;
    this.itineraryOptions = undefined;

    return this.http
      .post<SearchFlightInt>(`${this.urlApiSelfReaccom}/rest/sc/flight/search/v2`, body, {
        headers,
      })
      .pipe(catchError((error) => this.handlerError(error, TypeLoading.FULL_LOADING)));
  }

  setSSR(reservation: Reservation, actionIrops?: string, segmentIrops?: string): Observable<any> {
    const ssr = IropsMapper.makeSSR(actionIrops, segmentIrops);
    let headers;
    if (actionIrops) {
      headers = this.getHeaders(reservation, 'checkin', ssr);
    } else {
      headers = this.getHeaders(reservation);
    }

    return this.http.put(this.urlRemark, null, { headers }).pipe(
      map((response) => {
        return IropsMapper.handleWarnings(response);
      }),
      catchError((error) => this.handlerError(error, TypeLoading.FOOTER_LOADING))
    );
  }

  /**
   * Envía el remark al microservicio sb-remark.
   * Construye la cadena de remark usando IropsMapper.buildIropsRemark()
   * y la envía como body en un POST a urlRemark.
   */
  sendIropsRemark(reservation: Reservation, params: IropsRemarkParamsInt): Observable<any> {
    const remarkText: string = IropsMapper.buildIropsRemark(params);
    const headers: HttpHeaders = this.getHeaders(reservation, 'checkin', remarkText);

    return this.http.put(this.urlRemark, null, { headers }).pipe(
      map((response) => {
        return IropsMapper.handleWarnings(response);
      }),
      catchError((error) => this.handlerError(error, TypeLoading.FOOTER_LOADING))
    );
  }

  getHeadersGenerales(reservation: Reservation, irpopType = '') {
    let headers = new HttpHeaders({
      channel: 'KIOSK',
      execution: 'e1s1',
      store: 'mx',
      'x-transactionId': reservation.flightSummary.cartId,
      'Content-Type': 'application/json',
      CookieApp: this.iropsHeadersInt?.cookieApp || '',
      'Access-Control-Allow-Origin': '*',
      reservationCode: reservation.pnr,
      iropsType: irpopType,
      involuntaryExchange: 'false',
    });

    return headers;
  }

  getResponse(response) {
    const { warnings } = response;
    if (warnings && warnings.length > 0) {
      const [warning] = warnings;
      throw ErrorAdapter.adapt(warning);
    }

    return response;
  }

  reasingSeat(reservation: Reservation, newFlight?: FlightSummaryIrops) {
    const hasAnyPassengerWithoutSeat = reservation?.passengers?.some((p) => p.seats.length === 0);
    const flightForPayload = newFlight ?? this.flightSelect ?? this.bestMatchItinerary;
    const body = ApiMapper.getResquestAssingSeat(reservation, flightForPayload);
    const headers = this.getHeadersGenerales(reservation);
    if (hasAnyPassengerWithoutSeat) return of(null);

    return this.http
      .post(`${this.urlApiSelfReacomAncillary}/ac/reassign/seat`, body, { headers })
      .pipe(
        map((response) => {
          return this.getResponse(response);
        }),
        catchError((error) => this.handlerError(error, TypeLoading.FULL_LOADING))
      );
  }

  selectFlight(reservation: Reservation, id: number, iropType: string) {
    const headers = this.getHeadersGenerales(reservation, iropType);
    const params = new HttpParams().set('fareId', id);

    return this.http
      .get(`${this.urlApiSelfReaccom}/rest/sc/select/flight`, { headers, params })
      .pipe(
        map((res) => {
          return this.getResponse(res);
        }),
        catchError((error) => this.handlerError(error, TypeLoading.FULL_LOADING))
      );
  }

  handlerError(error: HttpErrorResponse, loading: TypeLoading = TypeLoading.FOOTER_LOADING) {
    this._loadingService.hideLoading(loading);
    const errorAdapt = ErrorAdapter.adapt(error.error);
    return throwError(errorAdapt);
  }

  getProducts(reservation: Reservation, exchangeType: string) {
    const headers = this.getHeaders(reservation);

    return this.http
      .get<SearchProductsInt>(`${this.urlApiSelfReaccom}/sc/products`, {
        headers,
        observe: 'response',
      })
      .pipe(
        map((response) => {
          return IropsMapper.getResponseWarningProducts(response);
        }),
        catchError((error) => this.handlerError(error, TypeLoading.FULL_LOADING))
      );
  }

  getAcillaries(reservation: Reservation) {
    const currentSegment = reservation.flightSummary?.currentSegment;
    const origin: string =
      currentSegment?.departureAirport || reservation.flightSummary?.departureAirport || '';
    const destination: string =
      currentSegment?.arrivalAirport || reservation.flightSummary?.arrivalAirport || '';
    const remarkText: string = IropsMapper.buildIropsRemark({
      action: 'EXCHNG',
      flow: 'INV',
      channel: 'KIO',
      window: 'OP',
      affectation: this.affectationType || 'MAJ',
      originDestination: `${origin}${destination}`,
    });
    const headers = this.getHeaders(reservation, 'checkin', remarkText);
    return this.http
      .get(`${this.urlReassociation}/ac/ancillaries/exchange/reassociation`, { headers })
      .pipe(
        map((res) => {
          return IropsMapper.getResponseReassociation(res);
        }),
        catchError((error) => {
          return this.handlerError(error);
        })
      );
  }

  completeIrops(elements: {
    reassignSeat: Observable<any>;
    ancillariesReassociation: Observable<any>;
    products: Observable<any>;
  }) {
    return forkJoin(elements).pipe(
      map((responses) => IropsMapper.getResponseWarningProducts(responses)),
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  purchase(reservation: Reservation) {
    const headers = this.getHeaders(reservation, 'EXCHANGE');
    const paymentRequired: boolean = false;

    const currentSegment = reservation.flightSummary?.currentSegment;
    const origin: string =
      currentSegment?.departureAirport || reservation.flightSummary?.departureAirport || '';
    const destination: string =
      currentSegment?.arrivalAirport || reservation.flightSummary?.arrivalAirport || '';
    const originDestination: string = `${origin}${destination}`;

    const remark: string = IropsMapper.buildIropsRemark({
      action: 'EXCHNG',
      flow: 'INV',
      channel: 'KIO',
      window: 'OP',
      affectation: this.affectationType || 'MAJ',
      originDestination,
    });

    const body = {
      paymentRequired,
      remark,
    };

    return this.http
      .post(`${this.urlPaymentBFF}/payment-revenue-dc-exchange`, body, { headers })
      .pipe(catchError((error) => this.handlerError(error, TypeLoading.FULL_LOADING)));
  }

  comercialConfirmation(reservation: Reservation) {
    const headers = this.getHeadersConfirmation(reservation);

    this._loadingService.showLoading(TypeLoading.FOOTER_LOADING);

    return this.http.get(`${this.urlConfirmation}`, { headers }).pipe(
      map((res) => {
        this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
        this.getResponse(res);
      }),
      catchError((error) => this.handlerError(error, TypeLoading.FOOTER_LOADING))
    );
  }

  /**
   * CI-21913: Confirma un schedule change (SC/WK/EXCH) a HK sin intervención del pasajero.
   * Encadena la confirmación comercial con el remark SCH2HK.
   * Si el back rechaza, el error se propaga para que el caller caiga al flujo IROPS estándar.
   */
  confirmScheduleChangeToHk(reservation: Reservation): Observable<any> {
    return this.comercialConfirmation(reservation).pipe(
      tap(() => (this.hasConfirmed = true)),
      switchMap(() => {
        const remarkParams = IropsMapper.buildRemarkParams(
          reservation,
          'SCH2HK',
          this.affectationType
        );
        return this.sendIropsRemark(reservation, remarkParams);
      })
    );
  }

  /** Se consume cabin micro service
   * @typeParam Reservation
   * @returns Observable<Cabin>
   *
   * */

  getCabin(reservation: PNRLookupAWSInt): Observable<CabinInfo[]> {
    const body: CabinRquest = ApiMapper.requestCabin(reservation);
    let headers = new HttpHeaders({
      channel: 'kiosk',
      'x-transactionId': reservation.transactionId,
      storefront: 'kiosk',
    });

    return this.http
      .post<CabinResponse>(`${environment.urlApiCheckin}/tc/cabin/names/byRouteRegion`, body, {
        headers,
      })
      .pipe(
        map((res) => {
          return ApiMapper.respCabin(res);
        }),
        catchError((error) => this.handlerError(error, TypeLoading.FOOTER_LOADING))
      );
  }
}
